import { useEffect, useMemo, useState } from "react";
import TagManager from "react-gtm-module";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import "./App.css";
import { tdkConfig } from "./enumerations/tdkConfig";
import { platformAdScript } from "./externalScript";
import "./plugins/i18n";
import { envChangeType, envType } from "./plugins/utils";
import RouterRender from "./router/index";
import FontSetting from "./styles/FontSetting";

function App() {
  const currentTheme = require(
    `./theme/${process.env.REACT_APP_PROD_NAME_DEV}/theme.json`
  );
  const defaultTheme = require(`./theme/defaultTheme.json`);
  const finalTheme = { ...defaultTheme, ...currentTheme };
  const { pathname } = useLocation();
  const [vh, setVh] = useState(1);

  const setTheme = () => {
    const r: any = document.querySelector(":root");
    const final = Object.entries(finalTheme);
    final.map((color) => {
      r?.style?.setProperty(color[0], color[1]);
    });
  };

  const initGTAG = () => {
    if (
      envChangeType("REACT_APP_GCODE") === "" ||
      envChangeType("REACT_APP_GCODE") === null ||
      envChangeType("REACT_APP_GCODE") === undefined
    )
      return;
    TagManager.initialize({
      gtmId: envChangeType("REACT_APP_GCODE"),
    });
  };

  const initStyle = () => {
    let _vh = window.innerHeight * 0.01;
    setVh(_vh);
    window.document.documentElement.style.setProperty("--vh", `${_vh}px`);
  };

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    headline: process.env.REACT_APP_META_TITLE,
    description: process.env.REACT_APP_META_DESCRIPTION,
    image: [
      `${process.env.REACT_APP_MAIN_URL}/images/contents/${process.env.REACT_APP_PROD_NAME_DEV}/common/webSeo.webp`,
    ],
    datePublished: "2025-07-04",
  };

  const renderScript = () => {
    if (process.env.REACT_APP_FB_PIXEL_ID) {
      const pixelArray = process.env.REACT_APP_FB_PIXEL_ID.split(",");
      return (
        <Helmet>
          <script type="text/javascript">
            {platformAdScript["FBpixel"].script({ id: pixelArray })}
          </script>
        </Helmet>
      );
    }
  };
  const renderNoScript = () => {
    if (process.env.REACT_APP_FB_PIXEL_ID) {
      const pixelArray = process.env.REACT_APP_FB_PIXEL_ID.split(",");
      return (
        <Helmet>
          <noscript>
            {platformAdScript["FBpixel"].noscript({ id: pixelArray })}
          </noscript>
        </Helmet>
      );
    }
  };
  const renderJsonLD = () => {
    if (envChangeType("REACT_APP_SHOW_JSONLD", envType.BOOLEAN) === true) {
      return (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(jsonLD)}</script>
        </Helmet>
      );
    }
  };

  // 監聽方向變化並調整畫面尺寸
  const handleOrientationChange = () => {
    // 延遲執行以確保方向變化完成
    setTimeout(() => {
      initStyle();
      // console.debug("Orientation changed, screen size adjusted");
    }, 100);
  };

  // 監聽視窗大小變化
  const handleResize = () => {
    initStyle();
    // console.debug("Window resized, screen size adjusted");
  };

  const currentTDK = useMemo(() => {
    return tdkConfig[pathname] || tdkConfig["/"];
  }, [pathname]);

  const renderTDK = () => {
    return (
      <Helmet prioritizeSeoTags>
        <title>{currentTDK.title}</title>
        <meta name="description" content={currentTDK.description} />
        <meta name="keywords" content={currentTDK.keywords} />
        <meta property="og:title" content={currentTDK.title} />
        <meta property="og:description" content={currentTDK.description} />
        <meta name="twitter:title" content={currentTDK.title} />
        <meta name="twitter:description" content={currentTDK.description} />
      </Helmet>
    );
  };

  useEffect(() => {
    initStyle();
    initGTAG();
    setTheme();

    // 添加方向變化監聽器
    window.addEventListener("orientationchange", handleOrientationChange);

    // 添加視窗大小變化監聽器
    window.addEventListener("resize", handleResize);

    // 清理函數
    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * SCROLL TESTING PURPOSE
   * DO NOT DELETE, OPEN WHEN DEBUG SCROLL ISSUE ONLY
   */
  function isScrollable(el: any) {
    return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
  }
  window.addEventListener(
    "scroll",
    function (e: any) {
      var el: any = e.target;
      while (el && el !== document && !isScrollable(el)) {
        el = el?.parent;
      }
      console.debug("Scrolled element: " + (el?.className || "document"));
    },
    true
  );

  return (
    <HelmetProvider>
      <div>
        {/* {renderScript()}
      {renderNoScript()} */}
        {renderTDK()}
        {renderJsonLD()}
        <FontSetting />
        <RouterRender />
      </div>
    </HelmetProvider>
  );
}

export default App;
